import { useEffect, useState } from 'react';
import ws from 'utils/ws';

/**
 * Fetch Triton eligible types from the backend once, and return a map keyed by
 * `${typeName}:v${integerVersion}` → alias. ModelSet display paths use this to
 * render a member's alias instead of its raw type+version — the server only
 * persists (typeName, version) because aliases are owned by insight2 and may
 * change over time, so resolving them at view time keeps labels fresh.
 *
 * The hook is best-effort: if the fetch fails the map stays empty and callers
 * fall back to `<typeName> v<version>` display, which is still unambiguous.
 */
export default function useTritonAliasMap(enabled = true) {
  const [aliasMap, setAliasMap] = useState({});

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    ws.get('/triton_models/eligible')
      .then(res => {
        if (cancelled) return;
        const result = res.data?.result || [];
        const next = {};
        for (const type of result) {
          for (const version of type.versions || []) {
            if (version && version.alias) {
              next[`${type.typeName}:v${version.integerVersion}`] = version.alias;
            }
          }
        }
        setAliasMap(next);
      })
      .catch(() => {
        /* Fall back to raw type/version display — handled by callers. */
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return aliasMap;
}
